/**
 * RecoilSync is recoil's very own synching util library.
 *
 * RecoilSyncLocalStorage is a `RecoilSync` provider, used to synch an Atom to localStorage. hence the name.
 * It attaches as an effect to an atom and requires a refine type checking (checkout Geocode Atoms to see usage).
 *
 * here is a quick example:
 * atom({ ..., effects: [syncEffect({ storeKey: 'geocode', refine: dict(bool()) })]})
 */

import { RecoilSync } from 'recoil-sync';

import type { ReactNode } from 'react';

interface IProps {
  storeKey: string;
  children: ReactNode;
}

export default function RecoilSyncLocalStorage({ storeKey, children }: IProps) {
  return (
    <RecoilSync
      storeKey={storeKey}
      read={(itemKey: string) => {
        const value = localStorage.getItem(itemKey);
        try {
          return value ? JSON.parse(value) : null;
        } catch (e) {
          /// when value in store is string but not json parable
          return value;
        }
      }}
      write={({ diff }) => {
        for (const [key, value] of diff) {
          const stringValue =
            typeof value === 'string' ? value : JSON.stringify(value);
          localStorage.setItem(key, stringValue);
        }
      }}
      listen={({ updateItem, updateAllKnownItems }) => {
        function onLocalStorageChange(e: StorageEvent) {
          const { key } = e;
          if (!key) return;

          const value = localStorage.getItem(key);
          let toStore;
          try {
            toStore = value ? JSON.parse(value) : null;
          } catch (e) {
            /// when value in store is string but not json parable
            toStore = value;
          }

          updateItem(key, toStore);
        }

        window.addEventListener('storage', onLocalStorageChange);
        return () => {
          window.removeEventListener('storage', onLocalStorageChange);
        };
      }}
    >
      {children}
    </RecoilSync>
  );
}

/**
 * Also we could've used a simple atom effect as so:
 *
 * p.s. Adopted from: https://recoiljs.org/docs/guides/atom-effects/#local-storage-persistence (2022/08/28)
 */

//  const localStorageEffect = key => ({setSelf, onSet}) => {
//   const savedValue = localStorage.getItem(key)
//   if (savedValue != null) {
//     setSelf(JSON.parse(savedValue));
//   }

//   onSet((newValue, _, isReset) => {
//     isReset
//       ? localStorage.removeItem(key)
//       : localStorage.setItem(key, JSON.stringify(newValue));
//   });
// };
